import React from 'react';
import { useFormContext, Controller, useFieldArray } from 'react-hook-form';
import type { IFormElement } from '../types';
import styles from './fields.module.css';

export interface MatrixDynamicFieldProps {
  element: IFormElement;
  questionNumber?: number;
}

export const MatrixDynamicField: React.FC<MatrixDynamicFieldProps> = ({
  element,
  questionNumber,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const error = errors[element.name];

  const columns = element.columns || [];
  const rowCount = element.rowCount || 2;
  const minRowCount = element.minRowCount || 0;
  const maxRowCount = element.maxRowCount || 10;

  const { fields, append, remove } = useFieldArray({
    control,
    name: element.name,
  });

  React.useEffect(() => {
    if (fields.length === 0 && rowCount > 0) {
      const initialRows = Array.from({ length: rowCount }, () => {
        const row: Record<string, string> = {};
        columns.forEach((col: any) => {
          const colName = typeof col === 'object' ? col.name : col;
          row[colName] = '';
        });
        return row;
      });
      initialRows.forEach((row) => append(row));
    }
  }, [append, columns, rowCount]);

  const addRow = () => {
    if (fields.length >= maxRowCount) return;
    const newRow: Record<string, string> = {};
    columns.forEach((col: any) => {
      const colName = typeof col === 'object' ? col.name : col;
      newRow[colName] = '';
    });
    append(newRow);
  };

  const removeRow = (index: number) => {
    if (fields.length <= minRowCount) return;
    remove(index);
  };

  const renderLabel = () => {
    if (!element.title) return null;
    return (
      <label
        className={`${styles.label} ${
          element.isRequired ? styles.required : ''
        }`}
      >
        {questionNumber && (
          <span className={styles.questionNumber}>{questionNumber}. </span>
        )}
        {element.title}
      </label>
    );
  };

  return (
    <div className={styles.formField}>
      {renderLabel()}
      {element.description && (
        <p className={styles.description}>{element.description}</p>
      )}
      <div style={{ overflowX: 'auto' }}>
        <table className={styles.matrixTable}>
          <thead>
            <tr>
              {columns.map((col: any, index: number) => {
                const colTitle =
                  typeof col === 'object' ? col.title || col.name : col;
                return <th key={index}>{colTitle}</th>;
              })}
              {!element.readOnly && <th style={{ width: '50px' }} />}
            </tr>
          </thead>
          <tbody>
            {fields.map((field, rowIndex) => (
              <tr key={field.id}>
                {columns.map((col: any, colIndex: number) => {
                  const colName = typeof col === 'object' ? col.name : col;
                  const colType =
                    typeof col === 'object' ? col.cellType || 'text' : 'text';
                  const choices =
                    typeof col === 'object' ? col.choices || [] : [];

                  return (
                    <td key={colIndex}>
                      <Controller
                        name={`${element.name}.${rowIndex}.${colName}`}
                        control={control}
                        defaultValue=""
                        render={({ field: cellField }) => {
                          if (colType === 'dropdown') {
                            return (
                              <select
                                {...cellField}
                                className={`${styles.input} ${styles.select}`}
                                disabled={element.readOnly}
                                aria-label={colName}
                              >
                                <option value="">Selecione</option>
                                {choices.map((choice: any, i: number) => {
                                  const v =
                                    typeof choice === 'object'
                                      ? choice.value
                                      : choice;
                                  const t =
                                    typeof choice === 'object'
                                      ? choice.text
                                      : choice;
                                  return (
                                    <option key={i} value={v}>
                                      {t}
                                    </option>
                                  );
                                })}
                              </select>
                            );
                          }
                          return (
                            <input
                              {...cellField}
                              type={colType === 'number' ? 'number' : 'text'}
                              className={styles.input}
                              disabled={element.readOnly}
                              aria-label={colName}
                            />
                          );
                        }}
                      />
                    </td>
                  );
                })}
                {!element.readOnly && (
                  <td>
                    <button
                      type="button"
                      onClick={() => removeRow(rowIndex)}
                      disabled={fields.length <= minRowCount}
                      style={{
                        padding: '0.25rem 0.5rem',
                        border: 'none',
                        background: 'transparent',
                        color: 'var(--df-error-color, #e60f41)',
                        cursor:
                          fields.length <= minRowCount
                            ? 'not-allowed'
                            : 'pointer',
                        opacity: fields.length <= minRowCount ? 0.5 : 1,
                      }}
                    >
                      ✕
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!element.readOnly && fields.length < maxRowCount && (
        <button
          type="button"
          onClick={addRow}
          style={{
            marginTop: '0.5rem',
            padding: '0.5rem 1rem',
            border: '1px solid var(--df-primary-color, #143971)',
            background: 'transparent',
            color: 'var(--df-primary-color, #143971)',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          {element.addRowText || '+ Adicionar linha'}
        </button>
      )}
      {error && (
        <p className={styles.errorMessage}>{error.message as string}</p>
      )}
    </div>
  );
};

export default MatrixDynamicField;
